import { Controller } from '@hotwired/stimulus'

// TASK 10.0: HTML escape utility for XSS prevention
function escapeHtml(str) {
  if (str == null) return ''
  const htmlEscapes = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#x27;',
    '/': '&#x2F;'
  }
  return String(str).replace(/[&<>"'/]/g, (char) => htmlEscapes[char])
}

/**
 * Block History Controller
 *
 * Handles version history slideout for website page blocks:
 * - Single block mode: View history for one block
 * - Page mode: View timeline of all edits across the page
 * - Allows preview and restore of any version
 */
export default class extends Controller {
  static targets = [
    'slideout',
    'backdrop',
    'historyList',
    'previewContainer',
    'previewContent',
    'loadingSpinner',
    'emptyState',
    'emptyTitle',
    'emptyMessage',
    'restoreBtn',
    'titleLabel',
    'subtitleLabel'
  ]

  static values = {
    blockHistoryUrl: String,
    pageHistoryUrl: String,
    rollbackUrl: String,
    pageId: String,
    blockId: String,
    blockType: String,
    mode: { type: String, default: 'block' } // 'block' or 'page'
  }

  connect() {
    this.selectedEntry = null
    this.historyData = null
    this.csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    // Close on escape key
    this.handleEscape = (e) => {
      if (e.key === 'Escape' && this.isOpen()) {
        this.close()
      }
    }
    document.addEventListener('keydown', this.handleEscape)
  }

  disconnect() {
    document.removeEventListener('keydown', this.handleEscape)
  }

  isOpen() {
    return this.hasSlideoutTarget && !this.slideoutTarget.classList.contains('translate-x-full')
  }

  // ==========================================
  // Page-wide Timeline Mode
  // ==========================================

  async openPageHistory(event) {
    event?.preventDefault()
    this.modeValue = 'page'
    this.blockIdValue = null

    // Update titles
    if (this.hasTitleLabelTarget) {
      this.titleLabelTarget.textContent = 'Edit History'
    }
    if (this.hasSubtitleLabelTarget) {
      this.subtitleLabelTarget.textContent = 'Recent changes across all blocks'
    }

    this.openSlideout()
    this.showLoading()

    try {
      const url = `${this.pageHistoryUrlValue}?page_id=${this.pageIdValue}`
      const response = await fetch(url, {
        headers: { 'Accept': 'application/json' }
      })

      if (!response.ok) throw new Error('Failed to fetch history')

      this.historyData = await response.json()
      this.renderPageHistory()
    } catch (error) {
      console.error('Error fetching page history:', error)
      this.showError('Failed to load edit history')
    }
  }

  renderPageHistory() {
    this.hideLoading()

    const { history, total_entries: _totalEntries } = this.historyData

    if (!history || history.length === 0) {
      // Set page-mode empty state text
      if (this.hasEmptyTitleTarget) {
        this.emptyTitleTarget.textContent = 'No edit history yet'
      }
      if (this.hasEmptyMessageTarget) {
        this.emptyMessageTarget.innerHTML = 'This page has no recorded edits yet.<br>Changes will be tracked automatically.'
      }
      if (this.hasEmptyStateTarget) this.emptyStateTarget.classList.remove('hidden')
      if (this.hasHistoryListTarget) this.historyListTarget.classList.add('hidden')
      return
    }

    if (this.hasHistoryListTarget) this.historyListTarget.classList.remove('hidden')
    if (this.hasEmptyStateTarget) this.emptyStateTarget.classList.add('hidden')

    // Build timeline HTML
    let html = ''
    let currentDate = null

    history.forEach((entry) => {
      const entryDate = this.formatDateOnly(entry.edited_at)

      // Add date separator if new day
      if (entryDate !== currentDate) {
        currentDate = entryDate
        html += `
          <div class="text-xs font-medium text-gray-500 uppercase tracking-wider px-2 py-2 bg-gray-50 -mx-4 px-4 sticky top-0">
            ${entryDate}
          </div>
        `
      }

      html += this.buildTimelineEntry(entry)
    })

    this.historyListTarget.innerHTML = html
  }

  // TASK 10.0: XSS-safe timeline entry construction
  buildTimelineEntry(entry) {
    const { block_id, block_type, section_title, version, edited_at, edited_by_name, is_current } = entry
    const formattedTime = this.formatTimeOnly(edited_at)
    // TASK 10.0: Escape user-generated content to prevent XSS
    const safeBlockLabel = escapeHtml(section_title || (block_type ? block_type.replace(/_/g, ' ') : 'block'))
    const safeEditorName = escapeHtml(edited_by_name || 'Unknown')
    const safeBlockId = escapeHtml(block_id)

    return `
      <div class="timeline-entry group relative py-3 px-3 -mx-3 rounded-lg cursor-pointer transition-all hover:bg-gray-50 border-l-4 ${is_current ? 'border-blue-500' : 'border-transparent hover:border-gray-300'}"
           data-block-id="${safeBlockId}"
           data-version="${version}"
           data-is-current="${is_current}"
           data-action="click->admin--block-history#selectTimelineEntry">
        <div class="flex items-start gap-3">
          <div class="flex-shrink-0 w-10 text-xs text-gray-400 pt-0.5">
            ${escapeHtml(formattedTime)}
          </div>
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2">
              <span class="font-medium text-sm text-gray-900 truncate">
                ${safeBlockLabel}
              </span>
              ${is_current ? '<span class="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">Current</span>' : ''}
            </div>
            <p class="text-xs text-gray-500 mt-0.5">
              v${version} by ${safeEditorName}
            </p>
          </div>
          ${!is_current ? `
            <button type="button"
                    class="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity p-1.5 text-blue-600 hover:bg-blue-100 rounded"
                    data-action="click->admin--block-history#restoreFromTimeline"
                    data-block-id="${safeBlockId}"
                    data-version="${version}"
                    title="Restore this version">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"/>
              </svg>
            </button>
          ` : ''}
        </div>
      </div>
    `
  }

  selectTimelineEntry(event) {
    if (event.target.closest('button[data-action*="restoreFromTimeline"]')) {
      return
    }

    const entry = event.currentTarget
    const blockId = entry.dataset.blockId
    const version = parseInt(entry.dataset.version, 10)
    const isCurrent = entry.dataset.isCurrent === 'true'

    // Update selection UI
    this.historyListTarget.querySelectorAll('.timeline-entry').forEach((e) => {
      e.classList.remove('ring-2', 'ring-blue-500', 'bg-blue-50')
    })
    entry.classList.add('ring-2', 'ring-blue-500', 'bg-blue-50')

    // Find the entry data
    const entryData = this.historyData.history.find((h) => h.block_id === blockId && h.version === version)

    if (entryData) {
      this.selectedEntry = { blockId, version, isCurrent }
      this.showPreview(entryData.rendered_html, entryData.content)

      // Show/hide restore button
      if (this.hasRestoreBtnTarget) {
        this.restoreBtnTarget.classList.toggle('hidden', isCurrent)
        if (!isCurrent) {
          this.restoreBtnTarget.dataset.blockId = blockId
          this.restoreBtnTarget.dataset.version = version
        }
      }
    }
  }

  async restoreFromTimeline(event) {
    event.stopPropagation()
    const blockId = event.currentTarget.dataset.blockId
    const version = parseInt(event.currentTarget.dataset.version, 10)

    await this.performRestore(blockId, version)
  }

  // ==========================================
  // Single Block Mode
  // ==========================================

  async open(event) {
    const blockId = event?.currentTarget?.dataset?.blockId || this.blockIdValue
    const blockType = event?.currentTarget?.dataset?.blockType || this.blockTypeValue

    if (!blockId) {
      console.error('Block ID is required to open history')
      return
    }

    this.modeValue = 'block'
    this.blockIdValue = blockId
    this.blockTypeValue = blockType

    // Update titles
    if (this.hasTitleLabelTarget) {
      this.titleLabelTarget.textContent = 'Version History'
    }
    if (this.hasSubtitleLabelTarget && blockType) {
      this.subtitleLabelTarget.textContent = `${blockType.replace(/_/g, ' ')} versions`
    }

    this.openSlideout()
    this.showLoading()

    try {
      const url = `${this.blockHistoryUrlValue}?block_id=${blockId}`
      const response = await fetch(url, {
        headers: { 'Accept': 'application/json' }
      })

      if (!response.ok) throw new Error('Failed to fetch history')

      this.historyData = await response.json()
      this.renderBlockHistory()
    } catch (error) {
      console.error('Error fetching block history:', error)
      this.showError('Failed to load version history')
    }
  }

  renderBlockHistory() {
    this.hideLoading()

    const { history, current_version, current_content, current_rendered_html, current_edited_at, current_edited_by_name } = this.historyData

    if ((!history || history.length === 0) && current_version === 1) {
      // Set block-mode empty state text
      if (this.hasEmptyTitleTarget) {
        this.emptyTitleTarget.textContent = 'No version history'
      }
      if (this.hasEmptyMessageTarget) {
        this.emptyMessageTarget.innerHTML = 'This block has not been edited yet.<br>Changes will be tracked automatically.'
      }
      if (this.hasEmptyStateTarget) this.emptyStateTarget.classList.remove('hidden')
      if (this.hasHistoryListTarget) this.historyListTarget.classList.add('hidden')
      return
    }

    if (this.hasHistoryListTarget) this.historyListTarget.classList.remove('hidden')
    if (this.hasEmptyStateTarget) this.emptyStateTarget.classList.add('hidden')

    let html = ''

    // Current version card
    html += this.buildVersionCard({
      version: current_version,
      content: current_content,
      rendered_html: current_rendered_html,
      edited_at: current_edited_at || new Date().toISOString(),
      edited_by_name: current_edited_by_name || 'Current',
      isCurrent: true
    })

    // Historical versions
    if (history && history.length > 0) {
      history.forEach((entry) => {
        html += this.buildVersionCard({
          ...entry,
          isCurrent: false
        })
      })
    }

    this.historyListTarget.innerHTML = html
  }

  // TASK 10.0: XSS-safe version card construction
  buildVersionCard(entry) {
    const { version, edited_at, edited_by_name, isCurrent } = entry
    const formattedDate = this.formatDate(edited_at)
    const relativeTime = this.formatRelativeTime(edited_at)
    // TASK 10.0: Escape user-generated content to prevent XSS
    const safeEditorName = escapeHtml(edited_by_name || 'Unknown')
    const safeFormattedDate = escapeHtml(formattedDate)
    const safeRelativeTime = escapeHtml(relativeTime)

    return `
      <div class="version-card group relative p-4 rounded-lg border cursor-pointer transition-all hover:shadow-sm
                  ${isCurrent ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'}"
           data-version="${version}"
           data-is-current="${isCurrent}"
           data-action="click->admin--block-history#selectVersion">
        <div class="flex items-start justify-between">
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2">
              <span class="font-medium text-sm text-gray-900">
                Version ${version}
              </span>
              ${isCurrent ? '<span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Current</span>' : ''}
            </div>
            <p class="text-xs text-gray-500 mt-1">${safeFormattedDate}</p>
            ${safeRelativeTime ? `<p class="text-xs text-gray-400">${safeRelativeTime}</p>` : ''}
          </div>
          <div class="flex items-center gap-2">
            <div class="text-right">
              <p class="text-xs font-medium text-gray-700">${safeEditorName}</p>
            </div>
            ${!isCurrent ? `
              <button type="button"
                      class="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 text-blue-600 hover:bg-blue-100 rounded"
                      data-action="click->admin--block-history#restoreVersion"
                      data-version="${version}"
                      title="Restore this version">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"/>
                </svg>
              </button>
            ` : ''}
          </div>
        </div>
      </div>
    `
  }

  selectVersion(event) {
    if (event.target.closest('button[data-action*="restoreVersion"]')) {
      return
    }

    const card = event.currentTarget
    const version = parseInt(card.dataset.version, 10)
    const isCurrent = card.dataset.isCurrent === 'true'

    // Update selection UI
    this.historyListTarget.querySelectorAll('.version-card').forEach((c) => {
      c.classList.remove('ring-2', 'ring-blue-500')
    })
    card.classList.add('ring-2', 'ring-blue-500')

    this.selectedEntry = { blockId: this.blockIdValue, version, isCurrent }

    // Show preview
    if (isCurrent) {
      this.showPreview(this.historyData.current_rendered_html, this.historyData.current_content)
    } else {
      const entry = this.historyData.history.find((h) => h.version === version)
      if (entry) {
        this.showPreview(entry.rendered_html, entry.content)
      }
    }

    // Show/hide restore button
    if (this.hasRestoreBtnTarget) {
      this.restoreBtnTarget.classList.toggle('hidden', isCurrent)
      if (!isCurrent) {
        this.restoreBtnTarget.dataset.blockId = this.blockIdValue
        this.restoreBtnTarget.dataset.version = version
      }
    }
  }

  async restoreVersion(event) {
    event.stopPropagation()
    const version = parseInt(event.currentTarget.dataset.version, 10)
    await this.performRestore(this.blockIdValue, version)
  }

  // ==========================================
  // Shared Methods
  // ==========================================

  openSlideout() {
    this.slideoutTarget.classList.remove('translate-x-full')
    this.slideoutTarget.classList.add('translate-x-0')
    if (this.hasBackdropTarget) {
      this.backdropTarget.classList.remove('opacity-0', 'pointer-events-none')
      this.backdropTarget.classList.add('opacity-100')
    }
    document.body.classList.add('overflow-hidden')
  }

  close() {
    this.slideoutTarget.classList.remove('translate-x-0')
    this.slideoutTarget.classList.add('translate-x-full')
    if (this.hasBackdropTarget) {
      this.backdropTarget.classList.add('opacity-0', 'pointer-events-none')
      this.backdropTarget.classList.remove('opacity-100')
    }
    document.body.classList.remove('overflow-hidden')
    this.selectedEntry = null
    this.clearPreview()
  }

  showLoading() {
    if (this.hasLoadingSpinnerTarget) this.loadingSpinnerTarget.classList.remove('hidden')
    if (this.hasHistoryListTarget) this.historyListTarget.classList.add('hidden')
    if (this.hasEmptyStateTarget) this.emptyStateTarget.classList.add('hidden')
  }

  hideLoading() {
    if (this.hasLoadingSpinnerTarget) this.loadingSpinnerTarget.classList.add('hidden')
  }

  // TASK 10.0: XSS-safe preview rendering
  // Uses sandboxed iframe for HTML content to prevent XSS
  showPreview(html, content) {
    if (!this.hasPreviewContainerTarget) return

    this.previewContainerTarget.classList.remove('hidden')

    // Clear previous content using safe DOM manipulation
    this.previewContentTarget.textContent = ''

    if (html) {
      // TASK 10.4: Use sandboxed iframe for HTML preview to prevent XSS
      const wrapper = document.createElement('div')
      wrapper.className = 'preview-wrapper relative rounded-lg border border-gray-200 overflow-hidden bg-white'

      const iframe = document.createElement('iframe')
      iframe.className = 'preview-iframe'
      iframe.style.width = '250%'
      iframe.style.height = '200px'
      iframe.style.transform = 'scale(0.4)'
      iframe.style.transformOrigin = 'top left'
      iframe.style.border = 'none'
      iframe.style.pointerEvents = 'none' // Prevent interactions

      // Sandbox prevents script execution - only allow styles
      iframe.sandbox = 'allow-same-origin'

      // Use srcdoc for secure content injection
      iframe.srcdoc = `<!DOCTYPE html><html><head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1">
        <style>body{margin:0;overflow:hidden;font-family:system-ui,-apple-system,sans-serif}*{box-sizing:border-box}img{max-width:100%;height:auto}</style>
      </head><body>${html}</body></html>`

      wrapper.appendChild(iframe)
      this.previewContentTarget.appendChild(wrapper)
    } else if (content) {
      const displayContent = { ...content }
      delete displayContent.ai_generated
      delete displayContent.ai_provider
      delete displayContent.ai_model
      delete displayContent.ai_prompt

      // TASK 10.4: Use safe DOM construction for JSON preview
      const wrapper = document.createElement('div')
      wrapper.className = 'p-4 bg-gray-50 rounded-lg border border-gray-200 overflow-auto max-h-[200px]'

      const pre = document.createElement('pre')
      pre.className = 'text-xs text-gray-700 whitespace-pre-wrap'
      // textContent is always safe - no HTML parsing
      pre.textContent = JSON.stringify(displayContent, null, 2)

      wrapper.appendChild(pre)
      this.previewContentTarget.appendChild(wrapper)
    } else {
      // Safe DOM construction for empty state
      const emptyDiv = document.createElement('div')
      emptyDiv.className = 'p-4 text-center text-gray-500 text-sm'
      emptyDiv.textContent = 'No preview available'
      this.previewContentTarget.appendChild(emptyDiv)
    }
  }

  clearPreview() {
    if (this.hasPreviewContainerTarget) {
      this.previewContainerTarget.classList.add('hidden')
      this.previewContentTarget.innerHTML = ''
    }
    if (this.hasRestoreBtnTarget) {
      this.restoreBtnTarget.classList.add('hidden')
    }
  }

  async performRestore(blockId, version) {
    const confirmed = await this.confirmRestore(version)
    if (!confirmed) return

    // Show loading on restore button
    const btn = this.restoreBtnTarget
    const originalHtml = btn?.innerHTML
    if (btn) {
      btn.disabled = true
      btn.innerHTML = `
        <svg class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        Restoring...
      `
    }

    try {
      const response = await fetch(this.rollbackUrlValue, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': this.csrfToken
        },
        body: JSON.stringify({
          block_id: blockId,
          version: version
        })
      })

      const data = await response.json()

      if (response.ok && data.success) {
        // Dispatch custom event for page-editor to update
        this.dispatch('restored', {
          detail: {
            blockId: blockId,
            content: data.content,
            renderedHtml: data.rendered_html,
            version: data.version
          }
        })

        this.showNotification(`Restored to version ${version}`, 'success')

        // Refresh the history view
        if (this.modeValue === 'page') {
          await this.openPageHistory()
        } else {
          await this.open({ currentTarget: { dataset: { blockId: blockId, blockType: this.blockTypeValue } } })
        }
      } else {
        throw new Error(data.error || 'Failed to restore version')
      }
    } catch (error) {
      console.error('Restore error:', error)
      this.showNotification(error.message || 'Failed to restore version', 'error')
    } finally {
      if (btn) {
        btn.disabled = false
        btn.innerHTML = originalHtml
      }
    }
  }

  async confirmRestore(version) {
    if (typeof Swal !== 'undefined') {
      const result = await Swal.fire({
        title: 'Restore Version?',
        text: `This will restore to version ${version}. Current content will be saved to history.`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#3b82f6',
        cancelButtonColor: '#6b7280',
        confirmButtonText: 'Restore',
        cancelButtonText: 'Cancel'
      })
      return result.isConfirmed
    }
    return confirm(`Restore to version ${version}? Current content will be saved to history.`)
  }

  showNotification(message, type = 'success') {
    const notification = document.createElement('div')
    notification.className = `fixed bottom-4 right-4 px-4 py-3 rounded-lg text-white ${type === 'success' ? 'bg-green-600' : 'bg-red-600'} shadow-lg z-[100] transition-opacity duration-300 flex items-center gap-2`
    notification.innerHTML = `
      ${type === 'success' ?
    '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg>' :
    '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>'
}
      <span>${message}</span>
    `
    document.body.appendChild(notification)

    setTimeout(() => {
      notification.classList.add('opacity-0')
      setTimeout(() => notification.remove(), 300)
    }, 3000)
  }

  showError(message) {
    this.hideLoading()
    if (this.hasHistoryListTarget) {
      this.historyListTarget.classList.remove('hidden')
      this.historyListTarget.innerHTML = `
        <div class="p-6 text-center">
          <svg class="w-12 h-12 mx-auto text-red-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
          </svg>
          <p class="text-gray-600 mb-3">${message}</p>
          <button type="button"
                  class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                  data-action="click->admin--block-history#retryFetch">
            Retry
          </button>
        </div>
      `
    }
  }

  retryFetch() {
    if (this.modeValue === 'page') {
      this.openPageHistory()
    } else {
      this.open({ currentTarget: { dataset: { blockId: this.blockIdValue, blockType: this.blockTypeValue } } })
    }
  }

  // ==========================================
  // Date Formatting
  // ==========================================

  formatDate(isoString) {
    if (!isoString) return 'Unknown date'
    try {
      const date = new Date(isoString)
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      })
    } catch {
      return 'Invalid date'
    }
  }

  formatDateOnly(isoString) {
    if (!isoString) return 'Unknown'
    try {
      const date = new Date(isoString)
      const today = new Date()
      const yesterday = new Date(today)
      yesterday.setDate(yesterday.getDate() - 1)

      if (date.toDateString() === today.toDateString()) {
        return 'Today'
      } if (date.toDateString() === yesterday.toDateString()) {
        return 'Yesterday'
      }

      return date.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'short',
        day: 'numeric'
      })
    } catch {
      return 'Unknown'
    }
  }

  formatTimeOnly(isoString) {
    if (!isoString) return ''
    try {
      const date = new Date(isoString)
      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit'
      })
    } catch {
      return ''
    }
  }

  formatRelativeTime(isoString) {
    if (!isoString) return ''
    try {
      const date = new Date(isoString)
      const now = new Date()
      const diffMs = now - date
      const diffMins = Math.floor(diffMs / 60000)
      const diffHours = Math.floor(diffMs / 3600000)
      const diffDays = Math.floor(diffMs / 86400000)

      if (diffMins < 1) return 'Just now'
      if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`
      if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`
      if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`
      return ''
    } catch {
      return ''
    }
  }
}
